import React, { useEffect } from 'react';

import type { ListUserData, NewList, UpdateList } from '@vlabs/api/luna-api/model';
import { InfoCircleIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Input } from '@vlabs/uikit/components/controls/input/Input';
import { useForm } from 'react-hook-form';

import { useCreateListFormTranslation } from './i18n';
import st from './ListForm.module.sass';

interface ListFormProps {
  defaultValues?: Partial<NewList> | Partial<UpdateList>;
  hint?: React.ReactNode;
  onSubmit: (values: NewList | UpdateList) => void;
  onCancel?: VoidFunction;
  context?: 'create' | 'update';
}

function ListForm({
  onSubmit,
  defaultValues = undefined,
  hint = undefined,
  onCancel = undefined,
  context = 'create',
}: ListFormProps): React.JSX.Element {
  const { t } = useCreateListFormTranslation();

  const form = useForm<{
    user_data: ListUserData;
  }>({
    defaultValues: {
      user_data: '',
      ...defaultValues,
    },
  });
  const { reset } = form;

  // подставляем название после асинхронной загрузки списка
  useEffect(() => {
    reset({ user_data: defaultValues?.user_data ?? '' });
  }, [defaultValues?.user_data, reset]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={st.Root}
        data-testid="listNameEditForm"
      >
        <h5>{t('title', { context })}</h5>

        <div className={st.InputWrapper}>
          <Input
            autoFocus
            id="user_data"
            label={t('user_data.label')}
            placeholder={t('user_data.placeholder')}
            {...form.register('user_data', {
              required: validate.required(),
              maxLength: validate.maxLength(128),
            })}
            errors={form.formState.errors}
          />
        </div>

        {!!hint && (
          <div className={st.Hint}>
            <InfoCircleIcon className={st.HintIcon} />
            <span>{hint}</span>
          </div>
        )}

        <div className={st.Actions}>
          <Button
            className={st.Button}
            data-testid="listNameEditForm.submitButton"
            type="submit"
          >
            {t('submit', { context })}
          </Button>

          <Button
            className={st.Button}
            data-testid="listNameEditForm.cancelButton"
            kind="secondary"
            onClick={onCancel}
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

export { ListForm };
